import { ComboChart } from '@euler/lib/charts/ComboChart';
import { ComboValueAxis } from '@euler/lib/charts/ComboChart/types';
import { HorizontalBarsChart } from '@euler/lib/charts/HorizontalBarsChart';
import { formatBiAxisTick, formatBiShortLabel } from '@euler/model/bi/format';
import { BiAxisSide, BiValueAxisStyle, BiVizType } from '@euler/model/bi/viz';
import { memo, useMemo } from 'react';
import { BiChartModel } from '../functions/biChartModel';
import { BiVizLook, kBiBarGapShares } from '../functions/biVizLook';
import { useBiChartTheme } from './useBiChartTheme';

type Cartesian = Extract<BiChartModel, { kind: 'cartesian' }>;

function valueAxis(style: BiValueAxisStyle): ComboValueAxis {
  return {
    show: style.show,
    title: style.label,
    min: style.min,
    max: style.max,
    format: formatBiAxisTick,
  };
}

/** A line, area, bar, stacked or horizontal bar chart, as styled. */
export const BiCartesianChart = memo(
  ({
    model,
    look,
    width,
    height,
  }: {
    model: Cartesian;
    look: BiVizLook;
    width: number;
    height: number;
  }) => {
    const theme = useBiChartTheme();
    const series = useMemo(
      () =>
        model.series.map(entry => ({
          key: entry.key,
          name: entry.name,
          type: entry.type,
          axis: entry.axis,
          color: entry.color,
          values: entry.values,
          labels: entry.labels,
        })),
      [model.series],
    );
    const yAxis = useMemo(() => valueAxis(look.axes.y), [look.axes.y]);
    const y2Axis = useMemo(() => valueAxis(look.axes.y2), [look.axes.y2]);
    const xAxis = useMemo(
      () => ({
        show: look.axes.x.show,
        title: look.axes.x.label,
        rotate: look.axes.x.labelRotate,
        time: model.timeAxis,
      }),
      [look.axes.x, model.timeAxis],
    );
    const bar = useMemo(
      () => ({
        cornerRadius: look.bar.cornerRadius,
        gap: kBiBarGapShares[look.bar.gap],
      }),
      [look.bar],
    );
    if (model.type === BiVizType.HorizontalBar)
      return (
        <HorizontalBarsChart
          width={width}
          categories={model.categories}
          series={series}
          categoryAxis={{ show: xAxis.show, title: xAxis.title }}
          valueAxis={yAxis}
          gridLines={look.gridLines}
          dataLabels={look.dataLabels}
          bar={bar}
          theme={theme}
        />
      );
    const hasRight = model.series.some(
      entry => entry.axis === BiAxisSide.Right,
    );
    return (
      <ComboChart
        width={width}
        height={height}
        categories={model.categories}
        series={series}
        stacked={model.stacked}
        xAxis={xAxis}
        yAxis={yAxis}
        y2Axis={hasRight ? y2Axis : undefined}
        gridLines={look.gridLines}
        dataLabels={look.dataLabels}
        compactLabel={formatBiShortLabel}
        line={look.line}
        bar={bar}
        theme={theme}
      />
    );
  },
);
